import React, { useMemo } from "react";
import { Card, CardHeader, Box, Typography } from "@mui/material";
import ReactApexChart from "react-apexcharts";
import PropTypes from "prop-types";
import "./UsageChart.css";
import moment from "moment";
import { fNumber } from "../../../utils/formatNumber";

UsageChart.propTypes = {
  duration: PropTypes.string,
  chartData: PropTypes.array,
  selectedMetrics: PropTypes.array,
};

// Curated human-friendly titles for chart metrics
const CARD_TITLES = {
  // Overview
  usersCount: "Total Users",
  totalUsers: "Total Users",
  activeUsers: "Active Users",
  periodActiveUsers: "Active Users",
  sessionsCount: "Sessions",
  totalSessions: "Sessions",
  sessions: "Sessions",

  // Recognition
  totalObjectCount: "Objects Detected",
  objectCount: "Objects Detected",
  totalCurrencyCount: "Currency Scans",
  currencyCount: "Currency Scans",
  totalWordCount: "Words Read",
  wordCount: "Words Read",
  totalImageCount: "Images Captured",
  imageCount: "Images Captured",

  // Modes
  totalTextModeCount: "Text Mode Usage",
  textModeCount: "Text Mode Usage",
  totalMiraCount: "MIRA Usage",
  miraCount: "MIRA Usage",
  totalTasksCount: "Tasks Completed",
  tasksCount: "Tasks Completed",
  totalTextToAudioCount: "Text to Audio",
  textToAudioCount: "Text to Audio",

  // AI Chat
  totalAiChatAskQuestionCount: "Questions Asked",
  aiChatAskQuestionCount: "Questions Asked",
  totalAiChatAnswerCount: "Answers Received",
  aiChatAnswerCount: "Answers Received",

  // Smart Eye
  totalSmartEyeAskQuestionCount: "Questions Asked",
  smartEyeAskQuestionCount: "Questions Asked",
  totalSmartEyeAnswerCount: "Answers Received",
  smartEyeAnswerCount: "Answers Received",
  totalSmartEyeCount: "Smart Eye Usage",
  smartEyeCount: "Smart Eye Usage",

  // Smart Image
  totalSmartImageAskQuestionCount: "Questions Asked",
  smartImageAskQuestionCount: "Questions Asked",
  totalSmartImageAnswerCount: "Answers Received",
  smartImageAnswerCount: "Answers Received",
  totalSmartImageCount: "Smart Image Usage",
  smartImageCount: "Smart Image Usage",

  // Feature Usage
  totalFeatureUsage: "Total Feature Usage",

  // Document
  totalDocumentPageCount: "Document Pages",
  readerPages: "Reader Pages",
  readerPagesCount: "Reader Pages",
  accessiblePagesCount: "Accessible Pages",
  totalDocumentUploadedCount: "Documents Uploaded",
  totalDocumentDeletedCount: "Documents Deleted",
  totalDocumentDeletedPages: "Pages Deleted",
  totalDocumentReadCount: "Documents Read",
  totalDocumentSharedCount: "Documents Shared",
  totalDocumentSummaryCount: "Summaries Generated",
  totalDocumentGenQuestionCount: "Document General Questions",
  documentGenQuestionCount: "Document General Questions",
  totalDocumentGenAnswerCount: "Document General Answers",
  documentGenAnswerCount: "Document General Answers",
  totalDocumentAddQuestionCount: "Document Questions Asked",
  documentAddQuestionCount: "Document Questions Asked",
  totalDocumentAddAnswerCount: "Document Answers Received",
  documentAddAnswerCount: "Document Answers Received",
  totalDocumentDownloadCount: "Documents Downloaded",
  totalDocumentInteractions: "Document Interactions",

  // Document Reader
  pdfProcessingStarted: "PDF Processing Started",
  firstPageProcessed: "First Page Processed",
  processPerPagePdf: "Per-Page Processing",
  previousPage: "Previous Page Clicks",
  nextPage: "Next Page Clicks",
  pageReadingStarted: "Reading Started",
  pageReadingCompleted: "Reading Completed",
  documentReaderInteraction: "Reader Interactions",

  // Camera Based Features
  cameraBasedFeatures: "Total Camera Features",
  totalObjectsDetected: "Objects Detected",
  objectImagesClicked: "Object Images Clicked",
  currencyUsage: "Currency Usage",
  textModeUsage: "Text Mode Usage",
  smartEyeUsage: "Smart Eye Usage",

  // Non Camera Based Features
  nonCameraBasedFeatures: "Total Non-Camera Features",
  miraUsage: "MIRA Usage",
  smartImageUsage: "Smart Image Usage",
  tutorUsage: "AI Tutor Usage",
  textToAudioUsage: "Text to Audio Usage",
};

// Helper to format camelCase to readable title
const formatKeyToTitle = (key) => {
  if (!key || typeof key !== "string") return "";

  if (CARD_TITLES[key]) {
    return CARD_TITLES[key];
  }

  let title = key
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();

  if (title.startsWith("Total ") && title !== "Total Users" && title !== "Total Feature Usage") {
    title = title.replace(/^Total\s+/, "");
  }

  return title;
};

// Get value by searching for the key in all categories of the item
const getNestedValue = (item, metricId) => {
  if (!item || !metricId || typeof metricId !== "string") return 0;
  const parts = metricId.split(".");
  const key = parts[1] || parts[0];

  if (!key) return 0;

  // Search for the key in all categories using find
  const foundCategory = Object.keys(item || {}).find(
    (category) =>
      item[category] &&
      typeof item[category] === "object" &&
      key in item[category],
  );

  const val = foundCategory ? item[foundCategory][key] ?? 0 : item[key] ?? 0;
  return typeof val === "number" && !Number.isNaN(val) ? val : Number(val) || 0;
};

export default function UsageChart({
  duration,
  chartData,
  selectedMetrics = [],
}) {
  // Generate series data for selected metrics from cards
  const seriesData = useMemo(() => {
    if (
      !Array.isArray(selectedMetrics) ||
      !selectedMetrics.length ||
      !Array.isArray(chartData) ||
      !chartData.length
    ) {
      return [];
    }

    return selectedMetrics
      .map((metricId) => {
        if (!metricId || typeof metricId !== "string") return null;
        const parts = metricId.split(".");
        const key = parts[1] || parts[0];
        if (!key) return null;
        const label = formatKeyToTitle(key);
        return {
          name: label || "Usage",
          data: chartData.map((item) => getNestedValue(item, metricId)),
        };
      })
      .filter((s) => s && Array.isArray(s.data) && s.data.length > 0);
  }, [selectedMetrics, chartData]);

  const chartLabels = useMemo(() => {
    if (!Array.isArray(chartData)) return [];
    return chartData.map((i) => {
      const date = new Date(i?.date?.split("T")?.[0]);
      if (duration === "monthly") {
        return moment(date).format("DD/MM");
      }
      if (duration === "yearly") {
        if (i?.month) {
          return `${i?.month?.split(" ")[0].slice(0, 3)} ${
            i?.month?.split(" ")[1] || ""
          }`.trim();
        }
        return moment(date).format("MMM YYYY");
      }
      if (duration === "threeMonth") {
        if (i?.weekLabel) {
          return i.weekLabel.split(", ")[0];
        }
        if (i?.weekStart) {
          return moment(new Date(i.weekStart)).format("DD/MM");
        }
        return moment(date).format("DD/MM");
      }
      return moment(date).format("DD/MM/YYYY");
    });
  }, [chartData, duration]);

  const chartOptions = useMemo(() => {
    return {
      chart: {
        type: "area",
        height: "500",
        zoom: {
          enabled: false,
          autoScaleYaxis: true,
        },
        toolbar: {
          show: false,
        },
      },
      labels: chartLabels,
      markers: {
        size: 0,
        style: "hollow",
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      legend: {
        show: true,
        position: "top",
      },
      tooltip: {
        shared: true,
        intersect: false,
        y: {
          formatter: (value) => fNumber(value),
        },
      },
      dataLabels: {
        enabled: false,
      },
      fill: {
        type: "gradient",
        gradient: {
          shadeIntensity: 1,
          opacityFrom: 0.7,
          opacityTo: 0.9,
          stops: [0, 100],
        },
      },
    };
  }, [chartLabels]);

  const hasData =
    seriesData.length > 0 && Array.isArray(chartData) && chartData.length > 0;

  return (
    <Card sx={{ height: "450px" }} data-testid="usageChart">
      <CardHeader title="Usage" />
      {!hasData ? (
        <Box
          sx={{
            height: "360px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      ) : (
        <ReactApexChart
          options={chartOptions}
          series={seriesData}
          type="area"
          height={398}
        />
      )}
    </Card>
  );
}